import type { UmamiEvent, UmamiEvents } from './umami-events';
import { parseUmamiWebsiteId } from './umami-website-id';

/**
 * Umami's collect endpoint, served from the marketing origin. Events are posted to it
 * directly: no tracker script runs in the dashboard, so no code from another origin ever
 * sees the sign-in form or a signed-in user's token. The endpoint needs only the website
 * id and builds the session from the request's IP and user agent, exactly as it does for
 * the tracker, so an event still joins the session that landed on pertento.ai with its
 * UTMs. Cross-origin posts are allowed by the marketing server (ACAO *).
 */
const COLLECT_URL = 'https://pertento.ai/_a/api/send';

/** Dev and staging send nothing, even with the production id in their .env. */
const APP_HOSTNAME = 'app.pertento.ai';

/**
 * Inlined at build time (scripts/build/dashboard.ts) and by the dev server (bunfig.toml). Unset
 * in dev, Bun leaves the expression as written and `process` does not exist in a browser: that
 * throw means "no id" and is not worth a log line. Not a `typeof process` guard, which would
 * survive the inlining and turn tracking off in production too.
 */
const readRawWebsiteId = (): string | undefined => {
  try {
    return process.env.BUN_PUBLIC_UMAMI_WEBSITE_ID;
  } catch {
    return undefined;
  }
};

const readWebsiteId = (): string | null => {
  try {
    return parseUmamiWebsiteId(readRawWebsiteId());
  } catch (error) {
    // The build already refuses a malformed id; a browser only stays silent, because
    // analytics must never break the page.
    console.error(error);
    return null;
  }
};

type TrackArgs<E extends UmamiEvent> = UmamiEvents[E] extends undefined ? [] : [data: UmamiEvents[E]];

/**
 * Sends one event and forgets it. Nothing else leaves: the name, the data the event
 * declares, the hostname and the path without its query (the auth routes carry one-time
 * secrets there). `keepalive`, so an event sent just before a navigation still lands.
 * Off without a website id or away from app.pertento.ai. Never throws.
 */
export const trackUmami = <E extends UmamiEvent>(event: E, ...args: TrackArgs<E>): void => {
  try {
    const website = readWebsiteId();
    if (!website || window.location.hostname !== APP_HOSTNAME) return;

    const [data] = args;
    const payload = { website, hostname: APP_HOSTNAME, url: window.location.pathname, name: event, data };

    fetch(COLLECT_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'event', payload }),
      credentials: 'omit',
      keepalive: true,
    }).catch(() => undefined);
  } catch (error) {
    console.error(`Could not send the Umami event ${event}:`, error);
  }
};
